/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useMemo, useState, useEffect } from 'react';
import { Checkbox, Button, Dropdown, Radio, Tooltip } from 'antd';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import {
  SCREENING_EVALUATION_OPTIONS,
  ScreeningEvaluationValue,
} from '~components/molecules/Tabs/TenderTabs/components/OffersNew/OffersScreening/screeningDemoData';
import {
  EVALUATION_OPTIONS,
  EvaluationValue,
} from '~components/molecules/Tabs/TenderTabs/components/OffersNew/OffersEvaluation/evaluationData';

interface VendorFilterPanelProps {
  screeningFilter: (number | null)[];
  evaluationFilter: (number | null)[];
  acceptedOffersFilter: boolean;
  vendorParticipationFilter: 'mix' | 'delivered' | 'no-offers';
  onScreeningChange: (values: (number | null)[]) => void;
  onEvaluationChange: (values: (number | null)[]) => void;
  onAcceptedOffersChange: (value: boolean) => void;
  onVendorParticipationChange: (
    value: 'mix' | 'delivered' | 'no-offers'
  ) => void;
}

export const VendorFilterPanel: React.FC<VendorFilterPanelProps> = ({
  screeningFilter,
  evaluationFilter,
  acceptedOffersFilter,
  vendorParticipationFilter,
  onScreeningChange,
  onEvaluationChange,
  onAcceptedOffersChange,
  onVendorParticipationChange,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [activePreset, setActivePreset] = useState<
    'accepted' | 'winning' | null
  >(null);

  const t = (id: string, def: string) =>
    intl.formatMessage({ id, defaultMessage: def });

  // Calculate if all screening options are selected
  const allScreeningSelected = useMemo(() => {
    const displayedValues: (number | null)[] = [0, 3, 4]; // Undecided, Accepted, Rejected
    return displayedValues.every((val) => screeningFilter.includes(val));
  }, [screeningFilter]);

  // Calculate if all evaluation options are selected
  const allEvaluationSelected = useMemo(() => {
    const allValues = EVALUATION_OPTIONS.map((opt) => opt.value);
    return allValues.every((val) => evaluationFilter.includes(val));
  }, [evaluationFilter]);

  // Calculate indeterminate states
  const screeningIndeterminate = useMemo(() => {
    const displayedFilters = screeningFilter.filter(
      (v) => v === 0 || v === 3 || v === 4
    );
    return displayedFilters.length > 0 && !allScreeningSelected;
  }, [screeningFilter, allScreeningSelected]);

  const evaluationIndeterminate = useMemo(() => {
    const visibleCount = evaluationFilter.filter((v) => v !== null).length;
    return visibleCount > 0 && !allEvaluationSelected;
  }, [evaluationFilter, allEvaluationSelected]);

  // Check if "Accepted Offers" preset filters match exactly
  const acceptedOffersFiltersMatch = useMemo(() => {
    return (
      vendorParticipationFilter === 'delivered' &&
      screeningFilter.length === 1 &&
      screeningFilter.includes(3) &&
      evaluationFilter.length === 0
    );
  }, [vendorParticipationFilter, screeningFilter, evaluationFilter]);

  // Check if "Winning Offers" preset filters match exactly
  const winningOffersFiltersMatch = useMemo(() => {
    return (
      vendorParticipationFilter === 'delivered' &&
      screeningFilter.length === 0 &&
      evaluationFilter.length === 2 &&
      evaluationFilter.includes(3) &&
      evaluationFilter.includes(5)
    );
  }, [vendorParticipationFilter, screeningFilter, evaluationFilter]);

  // Effect: Clear active preset when filters change manually
  useEffect(() => {
    if (activePreset === 'accepted' && !acceptedOffersFiltersMatch) {
      setActivePreset(null);
    }
    if (activePreset === 'winning' && !winningOffersFiltersMatch) {
      setActivePreset(null);
    }
  }, [activePreset, acceptedOffersFiltersMatch, winningOffersFiltersMatch]);

  const handleScreeningHeaderToggle = () => {
    if (allScreeningSelected) {
      onScreeningChange([]);
    } else {
      onScreeningChange([0, 3, 4]); // Undecided, Accepted, Rejected
    }
  };

  const handleEvaluationHeaderToggle = () => {
    if (allEvaluationSelected) {
      onEvaluationChange([]);
    } else {
      const allValues = EVALUATION_OPTIONS.map((opt) => opt.value);
      onEvaluationChange(allValues);
    }
  };

  const handleScreeningOptionToggle = (value: number | null) => {
    if (screeningFilter.includes(value)) {
      onScreeningChange(screeningFilter.filter((v) => v !== value));
    } else {
      onScreeningChange([...screeningFilter, value]);
    }
  };

  const handleEvaluationOptionToggle = (value: number) => {
    if (evaluationFilter.includes(value)) {
      onEvaluationChange(evaluationFilter.filter((v) => v !== value));
    } else {
      onEvaluationChange([...evaluationFilter, value]);
    }
  };

  const handleClearAll = () => {
    onScreeningChange([]);
    onEvaluationChange([]);
    setDropdownVisible(false);
  };

  // Handle "Accepted Offers" preset click
  const handleAcceptedOffersPreset = () => {
    if (activePreset === 'accepted') {
      // Reset to defaults if already active
      setActivePreset(null);
      onVendorParticipationChange('mix');
      onScreeningChange([0, 3, 4]);
      onEvaluationChange([0, 1, 2, 3, 4, 5, null]);
    } else {
      // Apply preset
      setActivePreset('accepted');
      onVendorParticipationChange('delivered');
      onScreeningChange([3]);
      onEvaluationChange([]); // RESET evaluation (empty)
    }
  };

  // Handle "Winning Offers" preset click
  const handleWinningOffersPreset = () => {
    if (activePreset === 'winning') {
      // Reset to defaults if already active
      setActivePreset(null);
      onVendorParticipationChange('mix');
      onScreeningChange([0, 3, 4]);
      onEvaluationChange([0, 1, 2, 3, 4, 5, null]);
    } else {
      // Apply preset
      setActivePreset('winning');
      onVendorParticipationChange('delivered');
      onScreeningChange([]); // RESET screening (empty)
      onEvaluationChange([3, 5]);
    }
  };

  const hasActiveFilters =
    screeningFilter.length > 0 || evaluationFilter.length > 0;

  // Calculate filter text for button
  const totalScreening = 3; // Unreviewed, Accepted, Rejected
  const totalEvaluation = EVALUATION_OPTIONS.length; // 6 visible options
  const screeningText =
    screeningFilter.length > 0
      ? `${t('import-tab-filter-screening', 'Screening')} (${
          screeningFilter.length
        }/${totalScreening})`
      : null;
  const evaluationText =
    evaluationFilter.length > 0
      ? `${t('import-tab-filter-evaluation', 'Evaluation')} (${
          evaluationFilter.filter((v) => v !== null).length
        }/${totalEvaluation})`
      : null;

  // Add participation filter text
  const participationText =
    vendorParticipationFilter === 'mix'
      ? t('import-tab-show-all-vendors', 'Show all Vendors')
      : vendorParticipationFilter === 'delivered'
      ? t('import-tab-delivered-offers', 'Delivered offers')
      : t('import-tab-no-offers', 'No offers');

  // Build button text
  const filterParts = [screeningText, evaluationText, participationText].filter(
    Boolean
  );
  const buttonText =
    filterParts.length > 0
      ? `${t('import-tab-filters', 'Filters')}, ${filterParts.join(', ')}`
      : t('import-tab-filters', 'Filters');

  // Get screening state label with i18n
  const getScreeningLabel = (value: ScreeningEvaluationValue): string => {
    const labelMap: Record<number, { id: string; default: string }> = {
      0: { id: 'import-tab-screening-undecided', default: 'Undecided' },
      1: {
        id: 'import-tab-screening-needs-attention',
        default: 'Needs attention',
      },
      2: { id: 'import-tab-screening-incomplete', default: 'Incomplete' },
      3: { id: 'import-tab-screening-accepted', default: 'Accepted' },
      4: { id: 'screening-row-rejected-option', default: 'Rejected' },
    };
    const label = labelMap[value];
    return label ? t(label.id, label.default) : String(value);
  };

  // Dropdown menu overlay
  const filterMenu = (
    <div css={styles.dropdownMenu}>
      <div css={styles.menuHeader}>
        <span css={styles.menuTitle}>{t('import-tab-filters', 'Filters')}</span>
        {hasActiveFilters && (
          <Button
            size="small"
            type="link"
            onClick={handleClearAll}
            css={styles.clearButton}>
            {t('import-tab-clear-all-filters', 'Clear All')}
          </Button>
        )}
      </div>

      <div css={styles.topFiltersRow}>
        <Tooltip
          title={t(
            'import-tab-accepted-offers-tooltip',
            'Show all vendors that had offers that was selected for evaluation'
          )}>
          <Button
            size="small"
            type={activePreset === 'accepted' ? 'primary' : 'default'}
            onClick={handleAcceptedOffersPreset}
            css={styles.toggleButton}>
            {t('import-tab-filter-accepted-offers', 'ACCEPTED OFFERS')}
          </Button>
        </Tooltip>

        <Tooltip
          title={t(
            'import-tab-winning-offers-tooltip',
            'Show all vendors that was won evaluation'
          )}>
          <Button
            size="small"
            type={activePreset === 'winning' ? 'primary' : 'default'}
            onClick={handleWinningOffersPreset}
            css={styles.toggleButton}>
            {t('import-tab-filter-winning-offers', 'WINNING OFFERS')}
          </Button>
        </Tooltip>

        {/* Placeholder buttons - commented out for future use
        <Button size="small" css={styles.placeholderButton}>
          Btn #3
        </Button>
        <Button size="small" css={styles.placeholderButton}>
          Btn #4
        </Button>
        */}
      </div>

      <div css={styles.participationRadioSection}>
        <Radio.Group
          value={vendorParticipationFilter}
          onChange={(e) => onVendorParticipationChange(e.target.value)}
          css={styles.participationRadioGroup}>
          <Radio value="mix" css={styles.radioOption}>
            {t('import-tab-show-all-vendors', 'Show all Vendors')}
          </Radio>
          <Radio value="delivered" css={styles.radioOption}>
            {t(
              'import-tab-show-delivered-vendors',
              'Show vendors who deliver offers'
            )}
          </Radio>
          <Radio value="no-offers" css={styles.radioOption}>
            {t(
              'import-tab-show-no-offers-vendors',
              "Show vendors who didn't deliver offers"
            )}
          </Radio>
        </Radio.Group>
      </div>

      {vendorParticipationFilter === 'delivered' && (
        <div css={styles.columnsContainer}>
          {/* Screening Column */}
          <div css={styles.column}>
            <div
              css={styles.columnHeader}
              onClick={handleScreeningHeaderToggle}>
              <Checkbox
                checked={allScreeningSelected}
                indeterminate={screeningIndeterminate}
                onChange={handleScreeningHeaderToggle}
                onClick={(e) => e.stopPropagation()}
              />
              <span css={styles.columnHeaderText}>
                {t('import-tab-filter-screening', 'Screening')}
              </span>
            </div>

            <div css={styles.optionsList}>
              <div
                key="undecided"
                css={styles.optionItem}
                onClick={() => handleScreeningOptionToggle(0)}>
                <Checkbox
                  checked={screeningFilter.includes(0)}
                  onChange={() => handleScreeningOptionToggle(0)}
                  onClick={(e) => e.stopPropagation()}
                />
                <span css={styles.optionLabel}>
                  {t('import-tab-screening-unreviewed', 'UNDECIDED')}
                </span>
                <span
                  css={[styles.colorIndicator, { background: '#8c8c8c' }]}
                />
              </div>
              {SCREENING_EVALUATION_OPTIONS.filter(
                (option) => option.value === 3 || option.value === 4
              ).map((option) => (
                <div
                  key={option.value}
                  css={styles.optionItem}
                  onClick={() => handleScreeningOptionToggle(option.value)}>
                  <Checkbox
                    checked={screeningFilter.includes(option.value)}
                    onChange={() => handleScreeningOptionToggle(option.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <span css={styles.optionLabel}>
                    {getScreeningLabel(option.value)}
                  </span>
                  <span
                    css={[styles.colorIndicator, { background: option.color }]}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Evaluation Column */}
          <div css={styles.column}>
            <div
              css={styles.columnHeader}
              onClick={handleEvaluationHeaderToggle}>
              <Checkbox
                checked={allEvaluationSelected}
                indeterminate={evaluationIndeterminate}
                onChange={handleEvaluationHeaderToggle}
                onClick={(e) => e.stopPropagation()}
              />
              <span css={styles.columnHeaderText}>
                {t('import-tab-filter-evaluation', 'Evaluation')}
              </span>
            </div>

            <div css={styles.optionsList}>
              {EVALUATION_OPTIONS.map((option) => (
                <div
                  key={option.value}
                  css={styles.optionItem}
                  onClick={() => handleEvaluationOptionToggle(option.value)}>
                  <Checkbox
                    checked={evaluationFilter.includes(option.value)}
                    onChange={() => handleEvaluationOptionToggle(option.value)}
                    onClick={(e) => e.stopPropagation()}
                  />

                  <span css={styles.optionLabel}>
                    {t(option.intlId, option.defaultLabel)}
                  </span>
                  <span
                    css={[styles.colorIndicator, { background: option.color }]}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {vendorParticipationFilter !== 'delivered' && (
        <div css={styles.noFiltersMessage}>
          {t(
            'import-tab-filters-disabled-participation',
            'Secondary filters are only available when showing vendors who delivered offers'
          )}
        </div>
      )}
    </div>
  );

  return (
    <Dropdown
      overlay={filterMenu}
      trigger={['click']}
      visible={dropdownVisible}
      onVisibleChange={setDropdownVisible}
      placement="bottomLeft">
      <Button css={styles.filterButton} style={{ minHeight: '40px' }}>
        <FontAwesomeIcon
          icon={['far', 'filter-list']}
          style={{ fontSize: '16px' }}
        />
        <span>{buttonText}</span>
        <FontAwesomeIcon
          icon={['fas', 'chevron-down']}
          css={styles.chevronIcon}
        />
      </Button>
    </Dropdown>
  );
};

const styleFn: ThemedStyleFn = () => ({
  filterButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    height: 40,
    padding: '0 16px',
    border: '1px solid #d1d5db',
    borderRadius: 8,
    background: '#ffffff',
    color: '#374151',
    fontSize: 14,
    fontWeight: 500,
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: '#2563eb',
      color: '#2563eb',
      background: '#f9fafb',
    },
    '& > span': {
      display: 'flex',
      alignItems: 'center',
    },
  },

  filterIcon: {
    fontSize: 14,
    color: '#6b7280',
  },

  chevronIcon: {
    fontSize: 11,
    marginLeft: 4,
    color: '#9ca3af',
  },

  dropdownMenu: {
    background: '#ffffff',
    borderRadius: 12,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    overflow: 'hidden',
    minWidth: 520,
    maxWidth: 600,
  },

  menuHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    borderBottom: '1px solid #f3f4f6',
    background: '#f9fafb',
  },

  menuTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: '#374151',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },

  clearButton: {
    padding: 0,
    height: 'auto',
    fontSize: 12,
    fontWeight: 600,
    color: '#2563eb',
    '&:hover': {
      color: '#1d4ed8',
    },
  },

  topFiltersRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 12,
    padding: '12px 16px',
    borderBottom: '1px solid #f3f4f6',
    background: '#fafbfc',
    alignItems: 'stretch',
  },

  toggleButton: {
    minWidth: 0,
    borderRadius: 6,
    fontSize: 10,
    fontWeight: 500,
    height: 28,
    padding: '0 8px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    transition: 'all 0.2s',
  },

  placeholderButton: {
    minWidth: 0,
    borderRadius: 6,
    fontSize: 11,
    height: 28,
    padding: '0 8px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  columnsContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 0,
  },

  column: {
    display: 'flex',
    flexDirection: 'column',
    '&:first-of-type': {
      borderRight: '1px solid #f3f4f6',
    },
  },

  columnHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '12px 16px',
    background: '#fafbfc',
    cursor: 'pointer',
    transition: 'background 0.2s ease',
    userSelect: 'none',
    borderBottom: '1px solid #f3f4f6',
    '&:hover': {
      background: '#f3f4f6',
    },
    '& .ant-checkbox-wrapper': {
      marginRight: 0,
    },
  },

  columnHeaderText: {
    fontSize: 13,
    fontWeight: 600,
    color: '#1f2937',
    flex: 1,
  },

  optionsList: {
    display: 'flex',
    flexDirection: 'column',
    padding: '8px 0',
    maxHeight: 300,
    overflowY: 'auto',
  },

  optionItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 16px',
    cursor: 'pointer',
    transition: 'background 0.15s ease',
    userSelect: 'none',
    position: 'relative',
    '&:hover': {
      background: '#f9fafb',
    },
    '& .ant-checkbox-wrapper': {
      marginRight: 0,
    },
  },

  optionLabel: {
    fontSize: 13,
    color: '#4b5563',
    flex: 1,
    fontWeight: 500,
  },

  colorIndicator: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    flexShrink: 0,
  },

  noFiltersMessage: {
    padding: '16px',
    textAlign: 'center',
    color: '#6b7280',
    fontSize: 13,
    fontStyle: 'italic',
  },

  participationRadioSection: {
    padding: '12px 16px',
    borderTop: '1px solid #e5e7eb',
    borderBottom: '1px solid #e5e7eb',
    background: '#fafafa',
  },

  participationRadioGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    width: '100%',
  },

  radioOption: {
    fontSize: 13,
    fontWeight: 500,
    color: '#374151',
    padding: '4px 0',
    margin: 0,
    '&:hover': {
      color: '#2563eb',
    },
    '& .ant-radio': {
      marginRight: 8,
    },
    '& .ant-radio-checked .ant-radio-inner': {
      borderColor: '#2563eb',
      background: '#2563eb',
    },
  },
});
